import { FixedExpensesForm } from '@/components/forms/FixedExpensesForm'
import { string, z } from 'zod'

export const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  remember_me: z.boolean().optional().default(false),
})

export const userSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters').optional(),
  role: z.string().min(1, 'Role is required'),
})

// User management form schema
export const createUserSchema = z.object({
  user_login: z.string().min(1, 'Username is required'),
  user_email: z.string().email('Invalid email address'),
  user_pass: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one digit')
    .regex(/[@$!%*#?&]/, 'Password must contain at least one special character (@$!%*#?&)'),
  employee_id: z.string().min(1, 'Employee ID is required'),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  position: z.string().optional(),
  user_role: z.string().min(1, 'Role is required'),
  user_status: z.number().int().min(0).max(2).default(1), // 0 = Inactive, 1 = Active, 2 = Suspended
})

export const updateUserSchema = z.object({
  user_login: z.string().min(1, 'Username is required'),
  user_email: z.string().email('Invalid email address'),
  user_pass: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one digit')
    .regex(/[@$!%*#?&]/, 'Password must contain at least one special character (@$!%*#?&)')
    .optional()
    .or(z.literal('')),
  employee_id: z.string().min(1, 'Employee ID is required'),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  position: z.string().optional(),
  user_role: z.string().min(1, 'Role is required'),
  user_status: z.number().int().min(0).max(2).default(1), // 0 = Inactive, 1 = Active, 2 = Suspended
})

export type CreateUserFormData = z.infer<typeof createUserSchema>
export type UpdateUserFormData = z.infer<typeof updateUserSchema>

// Booking form schema
export const createBookingSchema = z
  .object({
    reference_number: z.string().optional(),
    shipping_line_id: z.preprocess(
      (val) => (val === '' || (typeof val === 'number' && isNaN(val)) ? undefined : val),
      z
        .number({ required_error: 'Shipping Line is required', invalid_type_error: 'Shipping Line is required' })
        .int('Shipping Line must be a valid integer')
        .positive('Shipping Line must be a positive integer')
        .min(1, 'Shipping Line is required')
    ),
    cypa_id_from: z.preprocess(
      (val) => (val === '' || (typeof val === 'number' && isNaN(val)) ? undefined : val),
      z
        .number({ required_error: 'Received From is required', invalid_type_error: 'Received From is required' })
        .int('Received From must be a valid integer')
        .positive('Received From must be a positive integer')
        .min(1, 'Received From is required')
    ),
    cypa_id_to: z.preprocess(
      (val) => (val === '' || (typeof val === 'number' && isNaN(val)) ? undefined : val),
      z
        .number({ required_error: 'Delivered To is required', invalid_type_error: 'Delivered To is required' })
        .int('Delivered To must be a valid integer')
        .positive('Delivered To must be a positive integer')
        .min(1, 'Delivered To is required')
    ),
    expected_date: z
      .string()
      .min(1, 'Expected date is required'),
    expected_container: z.preprocess(
      (val) => (val === '' || (typeof val === 'number' && isNaN(val)) ? undefined : val),
      z
        .number({ required_error: 'Expected Container is required', invalid_type_error: 'Expected Container must be a number' })
        .int('Expected Container must be a valid integer')
        .positive('Expected Container must be a positive number')
        .min(1, 'Expected Container must be at least 1')
    ),
    vessel: z.string().optional(),
    remarks: z.enum(['SHIP IN', 'SHIP OUT'], {
      required_error: 'Remarks is required',
      invalid_type_error: 'Remarks must be SHIP IN or SHIP OUT',
    }),
  })
  .refine((data) => data.cypa_id_from !== data.cypa_id_to, {
    message: 'Received From and Delivered To must be different',
    path: ['cypa_id_to'],
  })

export const updateBookingSchema = createBookingSchema

export type CreateBookingFormData = z.infer<typeof createBookingSchema>
export type UpdateBookingFormData = z.infer<typeof updateBookingSchema>

// Waybill Detail form schema
export const createWaybillDetailSchema = z
  .object({
    waybill_number: z.string().min(1, 'Waybill number is required').max(255, 'Waybill number must not exceed 255 characters'),
    transaction_date: z.string().min(1, 'Transaction date is required'),
    shipping_line_id: z.preprocess(
      (val) => (val === '' || (typeof val === 'number' && isNaN(val)) ? undefined : val),
      z
        .number({ required_error: 'Shipping Line is required', invalid_type_error: 'Shipping Line is required' })
        .int('Shipping Line must be a valid integer')
        .positive('Shipping Line must be a positive integer')
        .min(1, 'Shipping Line is required')
    ),
    booking_id: z.preprocess(
      (val) => (val === '' || (typeof val === 'number' && isNaN(val)) ? undefined : val),
      z
        .number({ required_error: 'Booking ID is required', invalid_type_error: 'Booking ID is required' })
        .int('Booking ID must be a valid integer')
        .positive('Booking ID must be a positive integer')
        .min(1, 'Booking ID is required')
    ),
    driver_id: z
      .preprocess(
        (val) => (val === '' || (typeof val === 'number' && isNaN(val)) ? undefined : val),
        z
          .number({ required_error: 'Driver is required', invalid_type_error: 'Driver is required' })
          .int('Driver must be a valid integer')
          .positive('Driver must be a positive integer')
          .min(1, 'Driver is required')
      ),
    helper_id: z
      .preprocess(
        (val) => (val === '' || (typeof val === 'number' && isNaN(val)) || val === null ? undefined : val),
        z
          .number({ required_error: 'Helper is required', invalid_type_error: 'Helper is required' })
          .int('Helper must be a valid integer')
          .positive('Helper must be a positive integer')
          .min(1, 'Helper is required')
      ),
    container_size: z.string({required_error: 'Container size is required', invalid_type_error: 'Container size is required'}).min(1, 'Container size is required').max(255, 'Container size must not exceed 255 characters'),
    container_type: z.string().max(255, 'Container type must not exceed 255 characters').optional().nullable(),
    truck_plate_number: z.string({required_error: 'Truck plate number is required', invalid_type_error: 'Truck plate number is required'}).min(1, 'Truck plate number is required'),
    pickup_date: z.string().min(1, 'Pickup date is required'),
    delivered_date: z.string().min(1, 'Delivered date is required'),
    no_of_days: z
      .preprocess(
        (val) => (val === '' || (typeof val === 'number' && isNaN(val)) ? undefined : val),
        z
          .number({ required_error: 'Number of days is required', invalid_type_error: 'Number of days is required' })
          .int('Number of days must be a valid integer')
          .min(1, 'Number of days must be at least 1')
      ),
    requirements: z.string().max(255, 'Requirements must not exceed 255 characters').optional().nullable(),
    remarks: z.string().max(255, 'Remarks must not exceed 255 characters').optional().nullable(),
    stack_run: z.preprocess(
      (val) => (val === '' || (typeof val === 'number' && isNaN(val)) ? undefined : val),
      z
        .number({ required_error: 'Stack run is required', invalid_type_error: 'Stack run is required' })
        .min(0, 'Stack run must be 0 or greater')
    ),
    rate: z
      .preprocess(
        (val) => (val === '' || (typeof val === 'number' && isNaN(val)) ? undefined : val),
        z
          .number({ required_error: 'Rate is required', invalid_type_error: 'Rate is required' })
          .min(0, 'Rate must be 0 or greater')
      ),
    tax_percent: z
      .coerce
      .number()
      .min(0, 'Tax percent must be 0 or greater')
      .optional()
      .nullable(),
    has_vat: z.boolean().optional().nullable(),
    total_rate_per_client:  z
      .coerce
      .number()
      .min(0, 'Total rate per client must be 0 or greater')
      .default(0),
    fixed_expense_id: z
      .preprocess(
        (val) => (val === '' || (typeof val === 'number' && isNaN(val)) ? undefined : val),
        z
          .number({ required_error: 'Fixed expense is required', invalid_type_error: 'Fixed expense is required' })
          .int('Fixed expense must be a valid integer')
          .positive('Fixed expense must be a positive integer')
          .min(1, 'Fixed expense is required')
      ),
    post_expense_amount: z
      .coerce
      .number()
      .min(0, 'Post expense amount must be 0 or greater')
      .default(0),
    total_expense: z
      .coerce
      .number()
      .min(0, 'Total expense must be 0 or greater')
      .default(0),
    truck_trip_expense_id: z
      .preprocess(
        (val) => (val === '' || (typeof val === 'number' && isNaN(val)) || val === null ? undefined : val),
        z
          .number({ invalid_type_error: 'Truck trip expense must be a valid number' })
          .int('Truck trip expense must be a valid integer')
          .positive('Truck trip expense must be a positive integer')
      )
      .optional()
      .nullable(),
    actual_truck_trip_expense_amount: z
      .coerce
      .number()
      .min(0, 'Actual truck trip expense amount must be 0 or greater')
      .optional()
      .nullable(),
    diesel_expense_id: z
      .preprocess(
        (val) => (val === '' || (typeof val === 'number' && isNaN(val)) || val === null ? undefined : val),
        z
          .number({ invalid_type_error: 'Diesel expense must be a valid number' })
          .int('Diesel expense must be a valid integer')
          .positive('Diesel expense must be a positive integer')
      )
      .optional()
      .nullable(),
    diesel_expense_amount: z
      .coerce
      .number()
      .min(0, 'Diesel expense amount must be 0 or greater')
      .optional()
      .nullable(),
    purchase_order: z
      .string()
      .max(255, 'Vishner PO must not exceed 255 characters')
      .optional()
      .nullable(),
  })
  .refine(
    (data) => {
      if (data.pickup_date && data.delivered_date) {
        const pickup = new Date(data.pickup_date)
        const delivered = new Date(data.delivered_date)
        return delivered >= pickup
      }
      return true
    },
    {
      message: 'Delivered date must be after or equal to pickup date',
      path: ['delivered_date'],
    }
  )

export const updateWaybillDetailSchema = createWaybillDetailSchema

export type CreateWaybillDetailFormData = z.infer<typeof createWaybillDetailSchema>
export type UpdateWaybillDetailFormData = z.infer<typeof updateWaybillDetailSchema>

// Container yard form schema
export const createContainerYardSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required'),
  short_name: z
    .string()
    .optional()
    .nullable(),
  address: z
    .string()
    .min(1, 'Location is required'),
  contact_name: z
    .string()
    .optional()
    .nullable(),
  contact_mobile: z
    .string()
    .regex(/^[0-9\s+]*$/, 'Mobile number must only contain numbers, spaces, and special character (+)')
    .optional()
    .nullable(),
  landlines: z
    .preprocess((val) => (typeof val === 'string' ? val : ''), z.string())
    .transform((value) =>
      value.trim()
        ? value
            .split(',')
            .map((item) => item.trim())
            .filter((item) => item.length > 0)
        : []
    )
    .pipe(z.array(z.string().regex(/^[0-9\s+]*$/, 'Landline number must only contain numbers, spaces, and special character (+)')))
    .default([]),
  location_type: z
    .string()
    .min(1, 'Location type is required'),
  is_active: z.number().int().min(0).max(1).default(1),
  parent_id: z.number().nullable().optional(),
})

export const updateContainerYardSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required'),
  short_name: z
    .string()
    .optional()
    .nullable(),
  address: z
    .string()
    .min(1, 'Location is required'),
  contact_name: z
    .string()
    .optional()
    .nullable(),
  contact_mobile: z
    .string()
    .regex(/^[0-9\s+]*$/, 'Mobile number must only contain numbers, spaces, and special character (+)')
    .optional()
    .nullable(),
  landlines: z
    .preprocess((val) => (typeof val === 'string' ? val : ''), z.string())
    .transform((value) =>
      value.trim()
        ? value
            .split(',')
            .map((item) => item.trim())
            .filter((item) => item.length > 0)
        : []
    )
    .pipe(z.array(z.string().regex(/^[0-9\s+]*$/, 'Landline number must only contain numbers, spaces, and special character (+)')))
    .default([]),
  location_type: z
    .string()
    .min(1, 'Location type is required'),
  is_active: z.number().int().min(0).max(1).default(1),
  parent_id: z.number().nullable().optional(),
})

export type CreateContainerYardFormData = z.infer<typeof createContainerYardSchema>
export type UpdateContainerYardFormData = z.infer<typeof updateContainerYardSchema>

export const categorySchema = z.object({
  name: z.string().min(1, 'Name is required'),
  slug: z.string().min(1, 'Slug is required'),
  description: z.string().optional(),
  parent_id: z.number().optional(),
})

// Category management form schema
export const createCategorySchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .regex(/^[a-zA-Z0-9,&\-_\s]+$/, 'Name must only contain letters, numbers, spaces, and special characters (&, -, _)'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .regex(/^[a-zA-Z0-9,&\-_\s]+$/, 'Slug must only contain letters, numbers, spaces, and special characters (&, -, _)'),
  descriptions: z
    .string()
    .regex(/^[a-zA-Z0-9,&\-_\s]*$/, 'Description must only contain letters, numbers, spaces, and special characters (&, -, _)')
    .optional()
    .nullable(),
  parent_id: z.number().nullable().optional(),
})

export const updateCategorySchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .regex(/^[a-zA-Z0-9,&\-_\s]+$/, 'Name must only contain letters, numbers, spaces, and special characters (&, -, _)'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .regex(/^[a-zA-Z0-9,&\-_\s]+$/, 'Slug must only contain letters, numbers, spaces, and special characters (&, -, _)'),
  descriptions: z
    .string()
    .regex(/^[a-zA-Z0-9,&\-_\s]*$/, 'Description must only contain letters, numbers, spaces, and special characters (&, -, _)')
    .optional()
    .nullable(),
  parent_id: z.number().nullable().optional(),
})

export type CreateCategoryFormData = z.infer<typeof createCategorySchema>
export type UpdateCategoryFormData = z.infer<typeof updateCategorySchema>

export const tagSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  slug: z.string().min(1, 'Slug is required'),
  color: z.string().optional(),
})

// Tag management form schema
export const createTagSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .regex(/^[a-zA-Z0-9,&\-_\s]+$/, 'Name must only contain letters, numbers, spaces, and special characters (&, -, _)'),
  slug: z
    .string()
    .regex(/^[a-zA-Z0-9,&\-_\s]*$/, 'Slug must only contain letters, numbers, spaces, and special characters (&, -, _)')
    .optional()
    .nullable(),
  descriptions: z
    .string()
    .regex(/^[a-zA-Z0-9,&\-_\s]*$/, 'Description must only contain letters, numbers, spaces, and special characters (&, -, _)')
    .optional()
    .nullable(),
})

export const updateTagSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .regex(/^[a-zA-Z0-9,&\-_\s]+$/, 'Name must only contain letters, numbers, spaces, and special characters (&, -, _)'),
  slug: z
    .string()
    .regex(/^[a-zA-Z0-9,&\-_\s]*$/, 'Slug must only contain letters, numbers, spaces, and special characters (&, -, _)')
    .optional()
    .nullable(),
  descriptions: z
    .string()
    .regex(/^[a-zA-Z0-9,&\-_\s]*$/, 'Description must only contain letters, numbers, spaces, and special characters (&, -, _)')
    .optional()
    .nullable(),
})

export type CreateTagFormData = z.infer<typeof createTagSchema>
export type UpdateTagFormData = z.infer<typeof updateTagSchema>

// Page management form schema
export const createPageSchema = z.object({
  title: z.string().min(1, 'Title is required').max(255, 'Title must not exceed 255 characters'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .regex(/^[a-zA-Z0-9\-_\/]+$/, 'Slug can only contain letters, numbers, hyphens, underscores, and slashes'),
  content: z.string().optional().nullable(),
  layout_structure: z.any().optional().nullable(),
  layout: z.enum(['default', 'full-width', 'sidebar-left', 'sidebar-right', 'two-column', 'three-column']).default('default'),
  featured_image: z.string().max(500).optional().nullable(),
  meta_title: z.string().max(255).optional().nullable(),
  meta_description: z.string().max(500).optional().nullable(),
  status: z.enum(['draft', 'published', 'scheduled']).default('draft'),
  published_at: z.string().optional().nullable(),
  active: z.boolean().default(true),
})

export const updatePageSchema = z.object({
  title: z.string().min(1, 'Title is required').max(255, 'Title must not exceed 255 characters'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .regex(/^[a-zA-Z0-9\-_\/]+$/, 'Slug can only contain letters, numbers, hyphens, underscores, and slashes'),
  content: z.string().optional().nullable(),
  layout_structure: z.any().optional().nullable(),
  layout: z.enum(['default', 'full-width', 'sidebar-left', 'sidebar-right', 'two-column', 'three-column']).default('default'),
  featured_image: z.string().max(500).optional().nullable(),
  meta_title: z.string().max(255).optional().nullable(),
  meta_description: z.string().max(500).optional().nullable(),
  status: z.enum(['draft', 'published', 'scheduled']).default('draft'),
  published_at: z.string().optional().nullable(),
  active: z.boolean().default(true),
})

export type CreatePageFormData = z.infer<typeof createPageSchema>
export type UpdatePageFormData = z.infer<typeof updatePageSchema>

export const navigationSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  path: z.string().min(1, 'Path is required'),
  icon: z.string().optional(),
  parent_id: z.number().optional(),
  permissions: z.array(z.string()).optional(),
})

// Navigation management form schema
export const createNavigationSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  slug: z.string().min(1, 'Slug is required'),
  icon: z.string().optional(),
  parent_id: z.number().nullable().optional(),
  active: z.boolean().default(true),
  show_in_menu: z.boolean().default(true),
})

export const updateNavigationSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  slug: z.string().min(1, 'Slug is required'),
  icon: z.string().optional(),
  parent_id: z.number().nullable().optional(),
  active: z.boolean().default(true),
  show_in_menu: z.boolean().default(true),
})

export type CreateNavigationFormData = z.infer<typeof createNavigationSchema>
export type UpdateNavigationFormData = z.infer<typeof updateNavigationSchema>

// Role management form schema
export const createRoleSchema = z.object({
  name: z.string().min(1, 'Role name is required').max(255, 'Role name must not exceed 255 characters'),
  active: z.boolean().default(true),
  permissions: z.record(
    z.string(), // parent navigation id
    z.record(
      z.string(), // navigation id
      z.record(
        z.string(), // permission name (can_view, can_create, etc.)
        z.boolean()
      )
    )
  ).refine(
    (permissions) => {
      // Check if at least one permission is set to true
      for (const parentNav in permissions) {
        for (const navId in permissions[parentNav]) {
          for (const permName in permissions[parentNav][navId]) {
            if (permissions[parentNav][navId][permName] === true) {
              return true
            }
          }
        }
      }
      return false
    },
    { message: 'Please select at least one permission' }
  ),
})

export const createShippingLineSchema = z.object({
  name: z.preprocess(
    (val) => (typeof val === 'string' ? val.trim() : val),
    z.string().min(1, 'Name is required').max(255)
  ),
  short_name: z.preprocess(
    (val) => (typeof val === 'string' ? val.trim() || undefined : val),
    z.string().max(255).optional().nullable()
  ),
  email_address: z.preprocess(
    (val) => (typeof val === 'string' ? val.trim() : val),
    z.string().email('Invalid email address').min(1, 'Email Address is required').max(255)
  ),
  address: z.string().min(1, 'Address is required'),
  contact_name: z.string().min(1, 'Contact Name is required').max(255),
  contact_mobile: z.string().min(1, 'Contact Mobile is required').max(20),
  landlines: z.preprocess(
  (val) => {
    if (typeof val === 'string') {
      return val
        .split(',')
        .map(v => v.trim())
        .filter(Boolean)
    }
    return val
  },
  z.array(
    z.string().max(20, 'Landline must be at most 20 characters')
  ).optional()
),
  shipping_lines_template: z.array(z.number().int()).min(1, 'At least one Statement of Account Template is required'),
  transaction_information_template: z.array(z.number().int()).min(1, 'At least one Transaction Information Template is required'),
  fax_no: z.string().max(20).optional().nullable(),
  tin: z.string().max(50).min(1, 'Tin is required'),
})

export const createFixedExpensesSchema = z.object({
  shipping_line_id: z.preprocess(
    (val) => (val === '' || (typeof val === 'number' && isNaN(val)) ? undefined : val),
    z
      .number({ required_error: 'Shipping line is required', invalid_type_error: 'Shipping line is required' })
      .int()
      .positive('Shipping line is required')
  ),
  cypa_id_from: z.preprocess(
    (val) => (val === '' || (typeof val === 'number' && isNaN(val)) ? undefined : val),
    z
      .number({ required_error: 'Pickup point is required', invalid_type_error: 'Pickup point is required' })
      .int()
      .positive('Pickup point is required')
  ),
  cypa_id_to: z.preprocess(
    (val) => (val === '' || (typeof val === 'number' && isNaN(val)) ? undefined : val),
    z
      .number({ required_error: 'Delivery point is required', invalid_type_error: 'Delivery point is required' })
      .int()
      .positive('Delivery point is required')
  ),
  container_size: z.preprocess(
    (val) => (val === '' ? undefined : val),
    z.enum(['20ft', '40ft'], { required_error: 'Container size is required' })
  ),
  docs_fee: z.preprocess(
    (val) => (val === '' || (typeof val === 'number' && isNaN(val)) ? undefined : val),
    z
      .number({ required_error: 'Docs fee is required' })
      .min(0, 'Docs fee must be at least 0')
  ),
  stack_run: z.preprocess(
    (val) => (val === '' || (typeof val === 'number' && isNaN(val)) ? undefined : val),
    z
      .number({ required_error: 'Stack run is required' })
      .min(0, 'Stack run must be at least 0')
  ),
  expenses: z.preprocess(
    (val) => (val === '' || (typeof val === 'number' && isNaN(val)) ? undefined : val),
    z
      .number({ required_error: 'Expenses is required' })
      .min(0, 'Expenses must be at least 0')
  ),
  online_booking_fee: z.preprocess(
    (val) => (val === '' || (typeof val === 'number' && isNaN(val)) ? undefined : val),
    z
      .number({ invalid_type_error: 'Online booking fee must be a valid number' })
      .min(0, 'Online booking fee must be at least 0')
      .optional()
      .nullable()
  ),
}).superRefine((data, ctx) => {
  if (data.cypa_id_from === data.cypa_id_to) {
    ctx.addIssue({
      path: ['cypa_id_from'],
      message: 'From and To CYPA must be different',
      code: z.ZodIssueCode.custom,
    })

    ctx.addIssue({
      path: ['cypa_id_to'],
      message: 'From and To CYPA must be different',
      code: z.ZodIssueCode.custom,
    })
  }
})


export const updateRoleSchema = createRoleSchema
export const updateShippingLineSchema = createShippingLineSchema
export const updateFixedExpensesSchema = createFixedExpensesSchema

// Rate per Client form schema
export const ratePerClientSizes = ['20ft', '40ft', '20ft(offhire)', '40ft(offhire)'] as const

export const createRatePerClientSchema = z.object({
  shipping_line_id: z
    .number({ required_error: 'Shipping line is required' })
    .int()
    .positive('Shipping line is required'),
  no_of_days: z.preprocess(
    (val) => (val === '' || (typeof val === 'number' && isNaN(val)) ? undefined : val),
    z
      .number({ required_error: 'No. of days is required' })
      .int()
      .min(1, 'No. of days must be at least 1')
  ),
  requirements: z
    .string()
    .trim()
    .optional()
    .or(z.literal('')),
  remarks: z
    .string()
    .trim()
    .optional()
    .or(z.literal('')),
  cypa_id: z
    .number({ required_error: 'Container yard is required' })
    .int()
    .min(0, 'Container yard is required'),
  stack_run: z.preprocess(
    (val) => (val === '' || (typeof val === 'number' && isNaN(val)) ? undefined : val),
    z
      .number({ required_error: 'Stack run is required' })
      .min(0, 'Stack run must be at least 0')
  ),
  container_size: z.preprocess(
    (val) => (val === '' ? undefined : val),
    z.enum(ratePerClientSizes, { required_error: 'Container size is required' })
  ),
  rate: z.preprocess(
    (val) => (val === '' || (typeof val === 'number' && isNaN(val)) ? undefined : val),
    z.number({ required_error: 'Rate is required' }).min(0, 'Rate must be at least 0')
  ),
  has_vat: z.boolean().optional().nullable(),
  is_active: z.number().int().optional(),
})

{/* Single Inputs */}
// Add Budget
export const editIssuedBudgetSchema = z.object({
  shift: z.string(),
  dateIssued: z.string().min(1, 'Date Issued is required'),
  amount: z.number().min(0, 'Amount must be at least 0'),
  source: z.string().min(1, 'Source is required'),
})

export const editTruckTripExpenseSchema = z.object({
  shift: z.string(),
  helperID: z.number(),
  cashOnHand: z.number(),
  issuedCashAmount: z.number(),
  transactionDate: z.string(),
})

export const editPartsExpenseSchema = z.object({
  shift: z.string(),
  plateNumber: z.string(),
  receiptNO: z.string(),
  quantity: z.number(),
  article: z.string(),
  amountPerItem: z.number().min(0, 'Amount must be at least 0'),
  transactionDate: z.string(),
})

export const editFundsForStackRunSchema = z.object({
  shift: z.string(),
  remarks: z.string(),
  amount: z.number().min(0, 'Amount must be at least 0'),
  transactionDate: z.string(),
})

export const editDriverCashAdvanceSchema = z.object({
  amount: z.number().min(0, 'Amount must be at least 0'),
  transactionDate: z.string(),
  shift: z.string(),
  driverID: z.number(),
})

export const editHelperCashAdvanceSchema = z.object({
  amount: z.number().min(0, 'Amount must be at least 0'),
  transactionDate: z.string(),
  shift: z.string(),
  helperID: z.number(),
})

{/* Array Based Inptus */}
// Truck Trip Expense
const issuedBudgetSchema = z.object({
  expenseType: z.literal("issuedBudget"),
  shift: z.string(),
  dateIssued: z.string().min(1, 'Date Issued is required'),
  amount: z.number().min(0, 'Amount must be at least 0'),
  source: z.string().min(1, 'Source is required'),
})

const truckTripExpenseSchema =  z.object({
  expenseType: z.literal("truckTripExpense"),
  expenseItems: z.array(
    z.object({
      shift: z.string(),
      helperID: z.number(),
      cashOnHand: z.number(),
      issuedCashAmount: z.number(),
      transactionDate: z.string(),
    })
  )
})
// Parts Expense
const partsExpenseSchema =  z.object({
  expenseType: z.literal("partsExpense"),
  expenseItems: z.array(
    z.object({
      shift: z.string(),
      plateNumber: z.string(),
      receiptNO: z.string(),
      quantity: z.number(),
      article: z.string(),
      amountPerItem: z.number().min(0, 'Amount must be at least 0'),
      transactionDate: z.string(),
    })
  )
})
// Funds For Stack Run 
const fundsForStackRunSchema =  z.object({
  expenseType: z.literal("fundsForStackRun"),
  expenseItems: z.array(
      z.object({
      shift: z.string(),
      remarks: z.string(),
      amount: z.number().min(0, 'Amount must be at least 0'),
      transactionDate: z.string(),
    })
  )
})
// Advance Expense
const driverCashAdvanceSchema =  z.object({
  expenseType: z.literal("driverCashAdvance"),
  expenseItems: z.array(
    z.object({
      amount: z.number().min(0, 'Amount must be at least 0'),
      transactionDate: z.string(),
      shift: z.string(),
      driverID: z.number(),
    })
  )
})
const helperCashAdvanceSchema =  z.object({
  expenseType: z.literal("helperCashAdvance"),
  expenseItems: z.array(
    z.object({
      amount: z.number().min(0, 'Amount must be at least 0'),
      transactionDate: z.string(),
      shift: z.string(),
      helperID: z.number(),
    })
  )
})

export const createBudgetSchema =
  z.discriminatedUnion('expenseType', [
    issuedBudgetSchema,
    truckTripExpenseSchema,
    partsExpenseSchema,
    fundsForStackRunSchema,
    driverCashAdvanceSchema,
    helperCashAdvanceSchema,
  ])

export type EditIssuedBudgetFormData = z.infer<typeof editIssuedBudgetSchema>
export type EditTruckTripExpenseFormData = z.infer<typeof editTruckTripExpenseSchema>
export type EditPartsExpenseFormData = z.infer<typeof editPartsExpenseSchema>
export type EditFundsForStackRunFormData = z.infer<typeof editFundsForStackRunSchema> 
export type EditDriverCashAdvanceFormData = z.infer<typeof editDriverCashAdvanceSchema>
export type EditHelperCashAdvanceFormData = z.infer<typeof editHelperCashAdvanceSchema>

export const updateRatePerClientSchema = createRatePerClientSchema
export const updateBudgetSchema = createBudgetSchema

export type CreateRatePerClientFormData = z.infer<typeof createRatePerClientSchema>
export type UpdateRatePerClientFormData = z.infer<typeof updateRatePerClientSchema>

export type CreateRoleFormData = z.infer<typeof createRoleSchema>
export type UpdateRoleFormData = z.infer<typeof updateRoleSchema>

export type LoginFormData = z.infer<typeof loginSchema>
export type UserFormData = z.infer<typeof userSchema>
export type CategoryFormData = z.infer<typeof categorySchema>
export type TagFormData = z.infer<typeof tagSchema>
export type NavigationFormData = z.infer<typeof navigationSchema>

export type CreateShippingLineFormData = z.infer<typeof createShippingLineSchema>
export type UpdateShippingLineFormData = z.infer<typeof updateShippingLineSchema>

export type CreateFixedExpensesFormData = z.infer<typeof createFixedExpensesSchema>
export type UpdateFixedExpensesFormData = z.infer<typeof updateFixedExpensesSchema>

export type CreateBudgetFormData = z.infer<typeof createBudgetSchema>
export type UpdateBudgetFormData = z.infer<typeof updateBudgetSchema>

// SOA (Statement of Account) validation schema
// This schema validates the form fields only (dli_sa_number and work_order)
// shipping_line_id and booking_ids are added when submitting
export const createSoaSchema = z.object({
  dli_sa_number: z
    .string()
    .trim()
    .min(1, "DLI-SA Number is required")
    .max(255, "DLI-SA Number must not exceed 255 characters"),
  work_order: z
    .string()
    .trim()
    .max(255, "Work Order must not exceed 255 characters")
    .optional()
    .transform((val) => (val && val.trim() ? val.trim() : null)),
})

export type CreateSoaFormData = z.infer<typeof createSoaSchema>

// Send SOA email form schema (email required; cc, subject, body optional)
export const sendSoaEmailSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Email is required')
    .email('Invalid email address'),
  cc: z.array(z.string().email('Invalid email address')).optional().default([]),
  subject: z.string().trim().optional(),
  body: z.string().trim().optional(),
})

export type SendSoaEmailFormData = z.infer<typeof sendSoaEmailSchema>

// Billing Statement validation schemas (match backend: BillingStatementRequest / UpdateBillingStatementRequest)
const billingStatementBaseSchema = {
  payment_term: z.string().max(255, 'Payment term must not exceed 255 characters').optional().or(z.literal('')),
  ci_date: z.string().optional(),
  due_date: z.string().optional(),
  bus_style: z.string().max(255, 'Business style must not exceed 255 characters').optional().or(z.literal('')),
  has_details: z.boolean().default(false),
}

export const createBillingStatementSchema = z.object({
  billing_statement_no: z
    .string()
    .trim()
    .min(1, 'Billing statement number is required')
    .max(255, 'Billing statement number must not exceed 255 characters'),
  ...billingStatementBaseSchema,
})

export const updateBillingStatementSchema = createBillingStatementSchema

export type CreateBillingStatementFormData = z.infer<typeof createBillingStatementSchema>
export type UpdateBillingStatementFormData = z.infer<typeof updateBillingStatementSchema>